import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDataProvider } from 'react-admin';

interface OrgUnitOption {
  id: string;
  path: string;
  name: string;
  echelonType?: string;
}

interface ScopePickerProps {
  value: string;
  onChange: (path: string) => void;
  label?: string;
  disabled?: boolean;
}

/** Returns the depth of an org path (number of "/" separators minus the leading one). */
const pathDepth = (path: string): number => {
  if (!path || path === '/') return 0;
  return path.split('/').filter(Boolean).length;
};

export const ScopePicker = ({
  value,
  onChange,
  label = 'Org Path',
  disabled = false,
}: ScopePickerProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OrgUnitOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const dataProvider = useDataProvider();

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getList('org_units', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'path', order: 'ASC' },
        filter: {},
      });
      setOptions(data as OrgUnitOption[]);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load org units');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (path: string) => {
    onChange(path);
    setOpen(false);
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <TextField
          label={label}
          value={value || ''}
          size="small"
          disabled={disabled}
          InputProps={{ readOnly: true }}
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<SearchIcon />}
          onClick={handleOpen}
          disabled={disabled}
        >
          Browse
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Org Unit</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && (
            <Box p={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          {!loading && !error && options.length === 0 && (
            <Box p={2}>
              <Typography color="text.secondary">No org units found.</Typography>
            </Box>
          )}
          {!loading && !error && options.length > 0 && (
            <List dense disablePadding>
              {options.map((unit) => {
                const depth = pathDepth(unit.path);
                return (
                  <ListItemButton
                    key={unit.id}
                    selected={value === unit.path}
                    onClick={() => handleSelect(unit.path)}
                    sx={{ pl: 2 + depth * 2 }}
                  >
                    <ListItemText
                      primary={unit.name || unit.path}
                      secondary={unit.path}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {unit.echelonType && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, whiteSpace: 'nowrap' }}
                      >
                        {unit.echelonType}
                      </Typography>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
